import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Icon from '@mui/material/Icon';
import format from 'date-fns/format';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { checkNonEmptyOrNull, checkNullOrUndefined, isCustomer, isSupplier } from 'app/main/apps/common/AuraFunctions';
import clsx from 'clsx';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import _ from 'lodash';
import FuseUtils from '@fuse/utils';
import { authPermissions } from 'app/auth';
import safeHtml from 'app/shared-components/safeHtml';

function NotificationCard(props) {
    const { item, className, parent = null } = props;
    const variant = item?.variant || '';
    const navigate = useNavigate();
    const currentUser = useSelector(({ user }) => user);
    const isManage = currentUser && FuseUtils.hasPermission(authPermissions.manage, currentUser.role);
    const isNotificationsPage = parent && parent === 'notifications';

    const handleNavigation = (isComplete = false) => {
        let from = 'customer'
        let supplierParams = ''
        let isCompleteParam = ''
        let stage = `${item.stage}s`
        let title = ''
        if ((isCustomer(currentUser.role) || isSupplier(currentUser.role)) && !['rfq', 'quotes'].includes(stage)) {
            if (item.stage === 'rfd') {
                title = 'Rfp'
                stage = 'rfds'
            }
            if (item.stage === 'completed') {
                title = 'Rfp Completed'
                stage = 'completed'
            }
            if (item.stage === 'order') {
                title = isCustomer(currentUser.role) ? 'Orders' : 'Received Order'
                stage = 'orders'
            }
            if (item.stage === 'confirmed') {
                title = 'Confirmed Order'
                stage = 'confirmed'
            }
            if (item.stage === 'rfq') {
                stage = 'rfqs'
            }
        }
        if (item.stage === 'order' && isCustomer(currentUser.role)) {
            stage = 'orders'
        }
        if (item.stage === 'rfq') {
            if (isCustomer(currentUser.role) || isSupplier(currentUser.role)) {
                stage = 'rfqs'
            } else {
                stage = item.stage
            }
        }
        if(isCustomer(currentUser.role)) {
            from = 'customer';
        } else if(isSupplier(currentUser.role)) {
            from = 'supplier';
        } else {
            if (item.supplier_name) {
                from = 'supplier'
                supplierParams = `&supplier_name=${item.supplier_name}&supplier_id=${item.supplier_id}`
            }
            if (item?.sender_role_name !== "Customer" && item?.sender_role_name !== "Supplier") {
                from = 'internal'
            }
        }
        if (isComplete) {
            isCompleteParam = `&is_complete=${isComplete}&receiver_id=${item.receiver_id}`
        }
        if(isManage){
            const navigationId = item.supplier_name ? item.parent_order_id : item.order_id;
            if(!checkNullOrUndefined(navigationId)) {
                navigate(`/${stage}/${item.supplier_name ? item.parent_order_id : item.order_id}?tab=6&from=${from}${supplierParams}${isCompleteParam}&title=${title}`)
            }
        } else {
            navigate(`/${stage}/${item.order_id}?tab=6&from=${from}${supplierParams}${isCompleteParam}&title=${title}`)
        }
    }

    const getCardBgColor = (note) => {
        if(parent && parent === 'notes') return "#f1f4f9";

        // Notifications Section
        if(parent && parent === 'notifications') {
            if(note.stage && note.shipment_no) {
                return '#E9F9F9'; // Apply light green color, for the shipment message
            }
            if(!checkNonEmptyOrNull(note?.sender_role_name)) {
                const addedType = note?.sender_role_name?.toLowerCase();
                switch (addedType) {
                    case 'customer':
                        return '#E9F2FF'; // Apply light blue color, If the message is from customer
                    case 'supplier':
                        return '#FFECEB'; // Apply light red color, If the message is from supplier
                    default:
                        return '#F3F0FF'; // Use light purple color, If the message is from for any other employees
                }
            }
            return '#FFFFFF'; // Default white color added to check if sender_role_name is not coming
        }

        let colorCode = '#FFECEB' // Default color: light red color
        if (!isManage) colorCode = '#F3F0FF' // light purple color
        else if (note.customer_name) colorCode = '#E9F2FF'  // light blue color,
        return colorCode
    }

    const getOrderLabel = () => {
        if (item.stage === 'rfd') {
            return 'Rfp'
        }
        return _.capitalize(item.stage)
    }

    return (
        <Card
            className={clsx(
                'flex relative w-full rounded-16 p-20 min-h-64 shadow space-x-8 self-start',
                variant === 'success' && 'bg-green-600 text-white',
                variant === 'info' && 'bg-blue-700 text-white',
                variant === 'error' && 'bg-red-600 text-white',
                variant === 'warning' && 'bg-orange-600 text-white',
                className
            )}
            elevation={0}
            component={item.useRouter ? NavLinkAdapter : 'div'}
            to={item.link || ''}
            role={item.link && 'button'}
            sx={{ color: 'black', background: getCardBgColor(item) }}
        >
            {/* Added overflow-y-auto to fix mark as completed view */}
            <div className="flex flex-col flex-auto overflow-y-auto">
            {item.icon && !item.image && (
                <Box
                    sx={{ backgroundColor: 'background.default' }}
                    className="flex shrink-0 items-center justify-center w-32 h-32 mr-12 rounded-full"
                >
                    <FuseSvgIcon className="opacity-75" color="inherit">
                        {item.icon}
                    </FuseSvgIcon>
                </Box>
            )}

            {item.image && (
                <img
                    className="shrink-0 w-32 h-32 mr-12 rounded-full overflow-hidden object-cover object-center"
                    src={item.image}
                    alt="Notification"
                />
            )}

            <div className="flex flex-col flex-auto">
                <div className='flex flex-col md:flex-row justify-between mb-8'>
                    {/* Left hand section of header, Added By */}
                    {parent && parent === 'notes' && item.added_by && <Typography className="font-semibold line-clamp-1">Added By: {item.added_by}</Typography>}
                    {parent && parent === 'notes' && item.user_name && <Typography className="line-clamp-1">Added By: {item.user_name}</Typography>}

                    {/* Left hand section of header, Customer/Supplier Name */}
                    {!isNotificationsPage && item.customer_name &&  item.customer_name && isManage && <Typography className="font-semibold line-clamp-1">Customer Name: {item.customer_name}</Typography>}
                    {!isNotificationsPage && item.supplier_name && isManage && <Typography className="font-semibold line-clamp-1">Supplier Name: {item.supplier_name}</Typography>}

                    {isNotificationsPage && isManage &&
                        <>
                            {item?.sender_role_name === "Customer" && item.customer_name &&
                                <Typography className="font-semibold line-clamp-1">
                                    Customer Name: {item.customer_name}
                                </Typography>
                            }
                            {item?.sender_role_name === "Supplier" && item.supplier_name &&
                                <Typography className="font-semibold line-clamp-1">
                                    Supplier Name: {item.supplier_name}
                                </Typography>
                            }
                            {item?.sender_role_name !== "Customer" && item?.sender_role_name !== "Supplier" && item.sender_user_name &&
                                <Typography className="font-semibold line-clamp-1">
                                    Employee Name: {item.sender_user_name}
                                </Typography>
                            }
                        </>
                    }

                    {/* Right hand section of header, attachment icon, Order/RFQ/Quote No, laucnh icon section */}
                    {item.stage && item.order_no &&
                        <Typography className="flex flex-wrap items-center font-semibold cursor-pointer gap-8" onClick={() => handleNavigation(true)}>
                            {item?.is_has_attachment &&
                                <span>
                                    <FuseSvgIcon
                                        className="text-48 cursor-pointer"
                                        size={24}
                                        color="action"
                                    >
                                        material-twotone:attachment
                                    </FuseSvgIcon>
                                </span>}
                            {getOrderLabel()}: #{item.order_no}
                            <Icon fontSize='small'>launch</Icon>
                        </Typography>
                    }

                    {/* Right hand section of header, Date */}
                    {parent && parent === 'notes' && <Typography >{format(new Date(item.added_on || item.created_at), 'MMM dd, yyyy h:mm a')}</Typography>}

                    {/* Right hand section of header, attachment icon, Shipment No, laucnh icon section */}
                    {item.stage && item.shipment_no &&
                        <Typography className="flex flex-wrap items-center font-semibold cursor-pointer gap-8" onClick={() => props.updateCommunication(item)}>
                            {item?.is_has_attachment &&
                                <span className='mr-14'>
                                    <FuseSvgIcon
                                        className="text-48 cursor-pointer"
                                        size={24}
                                        color="action"
                                    >
                                        material-twotone:attachment
                                    </FuseSvgIcon>
                                </span>
                            }
                            Shipment: #{item.shipment_no}
                            <Icon fontSize='small'>launch</Icon>
                        </Typography>
                    }
                </div>
                 {/* Added overflow-y-auto to fix mark as completed view in Subject and Message portion */}
                <div className="flex-1 overflow-y-auto p-2 max-h-[20vh]">
                {
                    item?.message_type === "email" ? (
                        <div className='min-h-28 whitespace-pre-wrap'>
                            <div><span className='font-bold mr-2'>Subject:</span>{item?.subject}</div>
                            <div><span className='font-bold mr-2'>Message:</span>{item?.message}</div>
                        </div>
                    ) : (
                        <div className='min-h-28 whitespace-pre-wrap'>
                            {item.message && (
                                <div dangerouslySetInnerHTML={{ __html: safeHtml(item.message) }} />
                            )}
                            {item.comments && (
                                <div className='font-bold' dangerouslySetInnerHTML={{ __html: safeHtml(item.comments) }} />
                            )}
                            {parent && parent === 'notes' && item.note && (
                                <div dangerouslySetInnerHTML={{ __html: safeHtml(item.note) }} />
                            )}
                        </div>
                    )
                }
                </div>
                {item.last_updated_at && (
                    <div className='flex flex-col md:flex-row justify-between'>
                        <Typography className="mt-8 text-sm">
                            {format(new Date(item.last_updated_at), 'MMM dd, yyyy h:mm a')}
                        </Typography>
                        {!item?.shipment_no &&
                            <div>
                                <Button
                                    className='rounded-md bg-white border shadow-md mt-12 md:mt-0 '
                                    onClick={() => handleNavigation(true)}
                                    variant='contained'
                                    sx={{ color: 'black', height: "35px", minHeight:"35px" }}
                                >
                                    Reply and Mark as Completed
                                </Button>
                            </div>
                        }{item.stage &&item?.shipment_no &&
                            <div>
                                <Button
                                    className='rounded-md bg-white border shadow-md mt-12 md:mt-0 '
                                    onClick={() => props.updateCommunication(item)}
                                    variant='contained'
                                    sx={{ color: 'black', height: "35px", minHeight:"35px" }}
                                >
                                    Mark as Completed
                                </Button>
                            </div>
                        }
                    </div>
                )}
            </div>
            {item.children}
            </div>
        </Card>
    );
}

export default NotificationCard;
